import { APIv4Exception, Exception, ProductException } from '@scalefast/core';
import { of, zip } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import APIv4ResponseProcessor from '../../Shared/Infrastructure/Http/APIv4ResponsePipeProcessor';
import APIv4GetProductByIdResponseAdapter from './Adapters/APIv4GetProductByIdResponseAdapter';
import APIv4GetProductsByCategoriesResponseAdapter from './Adapters/APIv4GetProductsByCategoriesResponseAdapter';
import APIv4GetProductsByFeedResponseAdapter from './Adapters/APIv4GetProductsByFeedResponseAdapter';
import APIv4GetProductsResponseAdapter from './Adapters/APIv4GetProductsResponseAdapter';
import PRODUCT_EXCEPTIONS from './Exceptions/APIv4ProductExceptionMap';
import APIv4GetProductBySkuResponseAdapter from './Adapters/APIv4GetProductSkuResponseAdapter';
import APIv4MetaproductsAdapters from './Adapters/APIv4MetaproductsAdapters';
const ENDPOINT_GET_PRODUCT = '/product';
const ENDPOINT_GET_PRODUCT_BY_SKU = '/product/sku';
const ENDPOINT_GET_PRODUCTS = '/products';
const ENDPOINT_GET_CATEGORY = '/category';
// TODO: move this check to the handle of a product processor
function handleUserId(userID) {
  return tap(value => {
    const hasUserIdOnAPI = value.response_data && value.response_data.userID;
    const hasUserIdParam = Boolean(userID);
    if (hasUserIdOnAPI && hasUserIdParam && value.response_data.userID != userID) {
      throw new APIv4Exception('0', 'The product comes from a different UserID than the current one.');
    }
  });
}
class APIv4ProductRepository {
  constructor(context, samHttpClient, meCachisDependencyService) {
    this.context = context;
    this.samHttpClient = samHttpClient;
    this.meCachisDependencyService = meCachisDependencyService;
  }
  #requestErrorHandler(url, params, error) {
    if (error instanceof Exception) {
      throw new ProductException(url, params, error);
    } else {
      throw error;
    }
  }
  find(id, userID, metaproductColors = {}) {
    const url = `${this.context.baseUrl}${ENDPOINT_GET_PRODUCT}/${id}`;
    const params = {
      children_product_status: 'VALIDATED,INVISIBLE'
    };
    return this.samHttpClient.get(url, {
      params
    }).pipe(APIv4ResponseProcessor.handle(PRODUCT_EXCEPTIONS), handleUserId(userID), map(response => APIv4GetProductByIdResponseAdapter.toProduct(response, metaproductColors)), tap(product => this.meCachisDependencyService.addProduct(product.id)), catchError(error => this.#requestErrorHandler(url, params, error)));
  }
  findBySku(sku, territory, projectID, userID) {
    const url = `${this.context.baseUrl}${ENDPOINT_GET_PRODUCT_BY_SKU}/${sku}`;
    const params = {
      children_product_status: 'VALIDATED,INVISIBLE',
      country: territory,
      projectID: projectID
    };
    return this.samHttpClient.get(url, {
      params
    }).pipe(APIv4ResponseProcessor.handle(PRODUCT_EXCEPTIONS), handleUserId(userID), map(response => APIv4GetProductBySkuResponseAdapter.toProduct(response)), tap(product => this.meCachisDependencyService.addProduct(product.id)), catchError(error => this.#requestErrorHandler(url, params, error)));
  }
  findByReference(reference, clientID, userID, metaproductColors = {}) {
    const url = `${this.context.baseUrl}${ENDPOINT_GET_PRODUCTS}`;
    const params = {
      refID: reference,
      clientID
    };
    return this.samHttpClient.get(url, {
      params
    }).pipe(APIv4ResponseProcessor.handle(PRODUCT_EXCEPTIONS), handleUserId(userID), map(response => APIv4GetProductsResponseAdapter.toProductList(response, metaproductColors)), tap(products => this.meCachisDependencyService.addProducts(products)), catchError(error => this.#requestErrorHandler(url, params, error)));
  }
  findByFeedId(accessToken, feedId, limit, territory) {
    const url = `${this.context.baseUrl}/feed/${feedId}/products`;
    const params = {
      states: 'VALIDATED',
      access_token: accessToken,
      limit: limit || 4,
      country: territory || undefined
    };
    return this.samHttpClient.get(url, {
      params
    }).pipe(APIv4ResponseProcessor.handle(PRODUCT_EXCEPTIONS), map(response => APIv4GetProductsByFeedResponseAdapter.toProductList(response)), tap(products => this.meCachisDependencyService.addProducts(products)), catchError(error => this.#requestErrorHandler(url, params, error)));
  }
  findRelatedProducts(reference, clientID, userID, metaproductColors = {}) {
    switch (reference.type) {
      case "PRODUCT_LIST" /* RelatedProductReferenceType.PRODUCT_LIST */:
        return this.getRelatedProducts(reference.references, userID, metaproductColors);
      case "REFERENCE_LIST" /* RelatedProductReferenceType.REFERENCE_LIST */:
        return this.getRelatedProductsByReference(reference.references, clientID, userID, metaproductColors);
      default:
        throw new Error('RelatedProductReferenceType not supported, Feeds need to use findRelatedProductsByFeed ' + reference.type);
    }
  }
  getRelatedProducts(productIds, userID, metaproductColors = {}) {
    const products = [];
    for (const productId of productIds) {
      products.push(this.find(parseInt(productId), userID, metaproductColors));
    }
    return zip(...products);
  }
  getRelatedProductsByReference(references, clientID, userID, metaproductColors = {}) {
    const products = [];
    for (const reference of references) {
      products.push(this.findByReference(reference, clientID, userID, metaproductColors));
    }
    return zip(...products).pipe(map(products => products.reduce((accumulator, value) => accumulator.concat(value), [])));
  }
  findRelatedProductsByFeed(feedIds, accessToken, limit, territory) {
    const products = [];
    for (const feedId of feedIds) {
      products.push(this.findByFeedId(accessToken, parseInt(feedId), limit, territory));
    }
    return zip(...products).pipe(map(products => products.reduce((accumulator, value) => accumulator.concat(value), [])));
  }
  findByCategory(categoryId, clientID, limit, offset) {
    const url = `${this.context.baseUrl}${ENDPOINT_GET_CATEGORY}/${categoryId}/${ENDPOINT_GET_PRODUCTS}`;
    const params = {
      clientID,
      limit: limit ?? '50',
      offset: offset ?? '0'
    };
    return this.samHttpClient.get(url, {
      params
    }).pipe(APIv4ResponseProcessor.handle(PRODUCT_EXCEPTIONS), map(response => APIv4GetProductsByCategoriesResponseAdapter.toProductByCategories(response)), tap(products => this.meCachisDependencyService.addProducts(products)));
  }
  findProductWithMetaproducts(productID, territory, projectID, userID, metaproductColors) {
    return this.find(productID, userID, metaproductColors).pipe(switchMap(product => {
      if (product.sku) {
        return this.findBySku(product.sku, territory, projectID, userID).pipe(switchMap(productParent => {
          product.metaproductsV4 = APIv4MetaproductsAdapters.mapMetaproducts(product, productParent.specificV4, metaproductColors);
          return of(product);
        }));
      } else {
        return of(product);
      }
    }));
  }
}

export { APIv4ProductRepository as default };